import React, { useState, useCallback, useRef, useEffect, MutableRefObject } from 'react';
import {
  Search, ArrowLeftRight, Braces, WrapText,
  Undo2, Redo2, Settings, Save, Minus, Plus, Check,
} from 'lucide-react';
import Editor, { type OnMount } from '@monaco-editor/react';
import type { editor as MonacoEditor } from 'monaco-editor';
import { useSlotScreenGeneratorStore } from '@store/slotScreenGeneratorStore';

interface SystemPromptEditorProps {
  onDirtyChange?: (dirty: boolean) => void;
  saveRef?: MutableRefObject<(() => void) | null>;
}

// ─── Toolbar Button ──────────────────────────────────────
function ToolbarBtn({
  icon: Icon, label, onClick, active, disabled,
}: {
  icon: React.FC<{ size?: number; className?: string }>;
  label: string;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={label}
      className={`p-1.5 rounded transition-colors ${
        active
          ? 'bg-zinc-600 text-zinc-100'
          : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700'
      } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
    >
      <Icon size={14} />
    </button>
  );
}

// ─── Settings Popover ────────────────────────────────────
function SettingsPopover({ onClose }: { onClose: () => void }) {
  const prefs = useSlotScreenGeneratorStore(s => s.systemPromptEditorPrefs);
  const update = useSlotScreenGeneratorStore(s => s.updateSystemPromptEditorPrefs);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="absolute right-0 top-full mt-1 z-[1000] bg-zinc-800 border border-zinc-600 rounded-lg shadow-xl py-2 px-3 w-[220px]"
    >
      {/* Font Size */}
      <div className="flex items-center justify-between py-1.5">
        <span className="text-xs text-zinc-300">Font Size</span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => update({ fontSize: prefs.fontSize - 1 })}
            disabled={prefs.fontSize <= 10}
            className="p-0.5 text-zinc-400 hover:text-zinc-200 disabled:opacity-30 rounded hover:bg-zinc-700 transition-colors"
          >
            <Minus size={12} />
          </button>
          <span className="text-xs text-zinc-200 tabular-nums w-5 text-center">{prefs.fontSize}</span>
          <button
            onClick={() => update({ fontSize: prefs.fontSize + 1 })}
            disabled={prefs.fontSize >= 24}
            className="p-0.5 text-zinc-400 hover:text-zinc-200 disabled:opacity-30 rounded hover:bg-zinc-700 transition-colors"
          >
            <Plus size={12} />
          </button>
        </div>
      </div>

      {/* Tab Size */}
      <div className="flex items-center justify-between py-1.5">
        <span className="text-xs text-zinc-300">Tab Size</span>
        <div className="flex rounded overflow-hidden border border-zinc-600">
          {([2, 4] as const).map(n => (
            <button
              key={n}
              onClick={() => update({ tabSize: n })}
              className={`px-2.5 py-0.5 text-xs transition-colors ${
                prefs.tabSize === n
                  ? 'bg-zinc-600 text-zinc-100'
                  : 'text-zinc-400 hover:bg-zinc-700'
              }`}
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      {/* Line Numbers */}
      <div className="flex items-center justify-between py-1.5">
        <span className="text-xs text-zinc-300">Line Numbers</span>
        <button
          onClick={() => update({ lineNumbers: !prefs.lineNumbers })}
          className={`w-8 h-5 rounded-full transition-colors relative ${
            prefs.lineNumbers ? 'bg-blue-600' : 'bg-zinc-600'
          }`}
        >
          <div className={`absolute top-[3px] w-3.5 h-3.5 rounded-full bg-white transition-transform ${
            prefs.lineNumbers ? 'left-[calc(100%-1rem)]' : 'left-0.5'
          }`} />
        </button>
      </div>

      {/* Minimap */}
      <div className="flex items-center justify-between py-1.5">
        <span className="text-xs text-zinc-300">Minimap</span>
        <button
          onClick={() => update({ minimap: !prefs.minimap })}
          className={`w-8 h-5 rounded-full transition-colors relative ${
            prefs.minimap ? 'bg-blue-600' : 'bg-zinc-600'
          }`}
        >
          <div className={`absolute top-[3px] w-3.5 h-3.5 rounded-full bg-white transition-transform ${
            prefs.minimap ? 'left-[calc(100%-1rem)]' : 'left-0.5'
          }`} />
        </button>
      </div>
    </div>
  );
}

// ─── Main Editor ─────────────────────────────────────────
export function SystemPromptEditor({ onDirtyChange, saveRef }: SystemPromptEditorProps) {
  const systemPromptJson = useSlotScreenGeneratorStore(s => s.systemPromptJson);
  const updateJson = useSlotScreenGeneratorStore(s => s.updateSystemPromptJson);
  const prefs = useSlotScreenGeneratorStore(s => s.systemPromptEditorPrefs);
  const updatePrefs = useSlotScreenGeneratorStore(s => s.updateSystemPromptEditorPrefs);

  const [isDirty, setIsDirty] = useState(false);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [showSaved, setShowSaved] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [flashError, setFlashError] = useState(false);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const savedValueRef = useRef(systemPromptJson);
  const editorRef = useRef<MonacoEditor.IStandaloneCodeEditor | null>(null);

  // Notify parent of dirty state
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  // Sync from store when external changes happen
  useEffect(() => {
    const editor = editorRef.current;
    if (editor && systemPromptJson !== editor.getValue()) {
      editor.setValue(systemPromptJson);
      savedValueRef.current = systemPromptJson;
      setIsDirty(false);
    }
  }, [systemPromptJson]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    };
  }, []);

  // Flash saved indicator
  const flashSaved = useCallback(() => {
    setShowSaved(true);
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    savedTimerRef.current = setTimeout(() => setShowSaved(false), 2000);
  }, []);

  // Save function — reads directly from editor, not React state
  const save = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const val = editor.getValue();
    try {
      JSON.parse(val);
    } catch {
      // Flash the error bar to draw attention
      setFlashError(true);
      setTimeout(() => setFlashError(false), 1500);
      return;
    }
    updateJson(val);
    savedValueRef.current = val;
    setIsDirty(false);
    flashSaved();
  }, [updateJson, flashSaved]);

  // Expose save to parent
  useEffect(() => {
    if (saveRef) saveRef.current = save;
  }, [save, saveRef]);

  // Monaco mount — register change listener + Ctrl+S
  const handleMount: OnMount = useCallback((editor, monaco) => {
    editorRef.current = editor;

    // Track content changes for dirty state + JSON validation
    editor.onDidChangeModelContent(() => {
      const val = editor.getValue();
      setIsDirty(val !== savedValueRef.current);
      try {
        JSON.parse(val);
        setJsonError(null);
      } catch (err) {
        setJsonError((err as Error).message);
      }
    });

    // Ctrl+S — reads from editor directly, no stale closures
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
      const val = editor.getValue();
      try {
        JSON.parse(val);
      } catch {
        return;
      }
      useSlotScreenGeneratorStore.getState().updateSystemPromptJson(val);
      savedValueRef.current = val;
      setIsDirty(false);
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 2000);
    });
  }, []);

  // Toolbar actions
  const triggerFind = useCallback(() => {
    editorRef.current?.trigger('toolbar', 'actions.find', null);
  }, []);
  const triggerReplace = useCallback(() => {
    editorRef.current?.trigger('toolbar', 'editor.action.startFindReplaceAction', null);
  }, []);
  const triggerFormat = useCallback(() => {
    editorRef.current?.trigger('toolbar', 'editor.action.formatDocument', null);
  }, []);
  const toggleWrap = useCallback(() => {
    updatePrefs({ wordWrap: !prefs.wordWrap });
  }, [prefs.wordWrap, updatePrefs]);
  const triggerUndo = useCallback(() => {
    editorRef.current?.trigger('toolbar', 'undo', null);
  }, []);
  const triggerRedo = useCallback(() => {
    editorRef.current?.trigger('toolbar', 'redo', null);
  }, []);

  const hasValidJson = !jsonError;
  const canSave = isDirty;

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1 border-b border-zinc-700/50 flex-shrink-0">
        <ToolbarBtn icon={Search} label="Find (Ctrl+F)" onClick={triggerFind} />
        <ToolbarBtn icon={ArrowLeftRight} label="Replace (Ctrl+H)" onClick={triggerReplace} />
        <div className="w-px h-4 bg-zinc-700 mx-1" />
        <ToolbarBtn icon={Braces} label="Format JSON (Shift+Alt+F)" onClick={triggerFormat} />
        <ToolbarBtn icon={WrapText} label="Toggle Word Wrap" onClick={toggleWrap} active={prefs.wordWrap} />
        <div className="w-px h-4 bg-zinc-700 mx-1" />
        <ToolbarBtn icon={Undo2} label="Undo (Ctrl+Z)" onClick={triggerUndo} />
        <ToolbarBtn icon={Redo2} label="Redo (Ctrl+Y)" onClick={triggerRedo} />

        {/* Spacer */}
        <div className="flex-1" />

        {/* Status */}
        {isDirty && !showSaved && (
          <span className="text-xs text-yellow-400 font-medium mr-1">● Modified</span>
        )}
        {showSaved && (
          <span className="flex items-center gap-1 text-xs text-green-400 mr-1">
            <Check size={10} />
            Saved
          </span>
        )}

        {/* Settings */}
        <div className="relative">
          <ToolbarBtn
            icon={Settings}
            label="Editor Settings"
            onClick={() => setShowSettings(!showSettings)}
            active={showSettings}
          />
          {showSettings && <SettingsPopover onClose={() => setShowSettings(false)} />}
        </div>

        {/* Save */}
        <button
          onClick={save}
          disabled={!canSave}
          title={canSave && !hasValidJson ? 'Fix JSON errors before saving (Ctrl+S)' : 'Save (Ctrl+S)'}
          className={`flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded transition-colors ml-1 ${
            canSave && hasValidJson
              ? 'bg-blue-500 hover:bg-blue-400 text-white shadow-md shadow-blue-500/25'
              : canSave && !hasValidJson
                ? 'bg-amber-600 hover:bg-amber-500 text-white shadow-md shadow-amber-600/25'
                : 'bg-zinc-800 text-zinc-600 border border-zinc-700 cursor-not-allowed'
          }`}
        >
          <Save size={12} />
          Save
        </button>
      </div>

      {/* Monaco Editor — uncontrolled mode */}
      <div className="flex-1 min-h-0">
        <Editor
          language="json"
          theme="vs-dark"
          defaultValue={systemPromptJson}
          onMount={handleMount}
          options={{
            fixedOverflowWidgets: true,
            minimap: { enabled: prefs.minimap },
            scrollBeyondLastLine: false,
            fontSize: prefs.fontSize,
            tabSize: prefs.tabSize,
            lineNumbers: prefs.lineNumbers ? 'on' : 'off',
            wordWrap: prefs.wordWrap ? 'on' : 'off',
            automaticLayout: true,
            renderLineHighlight: 'line',
            padding: { top: 8 },
          }}
        />
      </div>

      {/* JSON error bar */}
      {jsonError && (
        <div className={`px-3 py-1.5 text-xs text-red-400 border-t border-red-500/20 flex-shrink-0 transition-colors ${
          flashError ? 'bg-red-900/40 text-red-300' : ''
        }`}>
          {flashError ? 'Cannot save: fix JSON errors first — ' : 'Invalid JSON: '}{jsonError}
        </div>
      )}
    </div>
  );
}
